<!--
Author: Alexey Usov (dax@xdax.ru, https://t.me/doubleaxe, https://github.com/doubleaxe)
Please don't remove this comment if you use unmodified file
-->
<script setup lang="ts">
import type {BlueprintItemModel, RecipeIOModel} from '@/scripts/model/store';
import {mdiSync, mdiLock, mdiAlert, mdiBullseye, mdiBullseyeArrow, mdiPriorityLow} from '@mdi/js';
import {formatIo, formatNumber} from '@/scripts/format';
import {__DEBUG__} from '@/scripts/debug';
import {computed} from 'vue';
import {Objective} from '@/scripts/types';
//fork addition: STARVED / BLOCKED diagnosis from the solved state
import {itemFlowStatus} from '@/scripts/flow-status';

const props = defineProps<{
    item: BlueprintItemModel;
}>();

const flowStatus = computed(() => itemFlowStatus(props.item));
const flowStatusTitle = computed(() => {
    const status = flowStatus.value;
    if(!status)
        return '';
    const io = status.io;
    if(status.kind === 'starved')
        return `${io.label}: linked supply ${formatIo(status.linkedRate, io)} of ${formatIo(status.targetRate, io)} needed — the factory pauses waiting for input`;
    return `${io.label}: only ${formatIo(status.linkedRate, io)} of ${formatIo(status.targetRate, io)} produced is taken away — the factory pauses with a full output`;
});

const hiddenIo = computed(() => {
    const _recipe = props.item?.selectedRecipe;
    const io = [...(_recipe?.invisibleInput() || []), ...(_recipe?.invisibleOutput() || [])];
    return io;
});
const objectiveIcon = computed(() => {
    switch(props.item.objective) {
        case Objective.Primary:
            return mdiBullseyeArrow;
        case Objective.Secondary:
            return mdiBullseye;
        case Objective.LowPriority:
            return mdiPriorityLow;
    }
    return '';
});

function hiddenIoTooltip(io: RecipeIOModel) {
    return `${io.label} ${formatIo(io.cpsSolvedTotal, io)}/${formatIo(io.cpsMaxTotal, io)}`;
}

</script>

<template>
    <div class="status-row bg-window-statusbar">
        <div class="title-text text-caption">
            <template v-if="props.item.chainContainsError">
                <v-icon :icon="mdiAlert" color="error" />
            </template>
            <template v-if="props.item.isLocked">
                <v-icon :icon="mdiLock" color="info" />
            </template>
            <template v-if="props.item.objective !== undefined">
                <v-icon :icon="objectiveIcon" color="info" />
            </template>
            <template v-if="props.item.partOfCycle">
                <v-icon :icon="mdiSync" color="warning" />
            </template>
            <template v-if="(props.item.solvedCount !== undefined)">
                {{ (props.item.count ? formatNumber((props.item.solvedCount * 100) / props.item.count) : '0') + '%' }}
                {{ formatNumber(props.item.solvedCount) + ' / ' }}
            </template>
            {{ formatNumber(props.item.count) }}
        </div>
        <div v-if="__DEBUG__" class="float-right mr-1 text-caption">
            {{ props.item.key }}
        </div>
        <div class="float-right mr-1 mt-1 d-flex hidden-io">
            <template v-for="io in hiddenIo" :key="io.key">
                <div class="mr-1">
                    <icon-component :image="io.image" :data-tooltip="hiddenIoTooltip(io)" />
                </div>
            </template>
        </div>
    </div>
    <!-- fork addition: STARVED / BLOCKED badge — sits in the footer
         zone (io rows move with the flow orientation, this doesn't) -->
    <div
        v-if="flowStatus"
        class="flow-badge text-caption"
        :class="`flow-badge-${flowStatus.kind}`"
        :title="flowStatusTitle"
    >
        {{ flowStatus.kind === 'starved' ? '▼ STARVED' : '▲ BLOCKED' }} · {{ flowStatus.io.label }}
    </div>
</template>

<style scoped>
.status-row {
    display: block;
    position: relative;
    height: 1.2rem;
    overflow: hidden;
}
.hidden-io {
    transform: scale(0.4);
    transform-origin: top right;
}
/* fork addition: STARVED (amber) / BLOCKED (orange-red) badge row
   under the status line — solid fills read on both card themes */
.flow-badge {
    padding: 1px 6px;
    line-height: 15px;
    font-weight: 700;
    letter-spacing: 0.03em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.flow-badge-starved {
    background-color: #ffb300;
    color: #212121;
}
.flow-badge-blocked {
    background-color: #d84315;
    color: #fff;
}
</style>
